import React, { useState, useCallback, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { useTheme, type ThemeColors } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Z_INDEX, zStyle } from '../../../lib/zLayers';
import { extractRecipientEmail, isValidRecipientToken } from '../utils/recipientUtils';
import type { ComposeRecipientSuggestion } from '../utils/composeRecipientSuggestions';
import { useRecipientSuggestionUi } from '../hooks/useRecipientSuggestionUi';
import RecipientSuggestionList, {
  recipientSuggestionOptionId,
} from './RecipientSuggestionList';

export type RecipientField = 'to' | 'cc' | 'bcc';

function parseEmails(value: string): string[] {
  return value
    .split(/[,;]/)
    .map((e) => e.trim())
    .filter(Boolean);
}

function joinEmails(emails: string[]): string {
  return emails.join(', ');
}

interface RecipientPillEditInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  ariaLabel: string;
  colors: ThemeColors;
}

const RecipientPillEditInput: React.FC<RecipientPillEditInputProps> = ({
  value,
  onChange,
  onBlur,
  onKeyDown,
  ariaLabel,
  colors,
}) => {
  const measureRef = useRef<HTMLSpanElement>(null);
  const [inputWidth, setInputWidth] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el) return;
    // Match visible text width plus horizontal padding (px-2).
    setInputWidth(Math.ceil(el.getBoundingClientRect().width) + 16);
  }, [value]);

  return (
    <span className="relative inline-flex max-w-full align-middle">
      <span
        ref={measureRef}
        aria-hidden
        className="invisible absolute whitespace-pre text-sm pointer-events-none"
      >
        {value || '\u00a0'}
      </span>
      <input
        type="text"
        autoFocus
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        style={inputWidth ? { width: `${inputWidth}px` } : undefined}
        className={cn(
          'inline-block max-w-full rounded-md border px-2 py-1 text-sm outline-none',
          'border-primary/50 ring-1 ring-primary/30 bg-white dark:bg-white',
          colors.text,
        )}
        aria-label={ariaLabel}
      />
    </span>
  );
};

export interface RecipientSelectorProps {
  to: string;
  cc: string;
  bcc: string;
  onChangeTo: (value: string) => void;
  onChangeCc: (value: string) => void;
  onChangeBcc: (value: string) => void;
  toInvalid?: boolean;
  disabled?: boolean;
  showCc: boolean;
  showBcc: boolean;
  onToggleCc: () => void;
  onToggleBcc: () => void;
  placeholderTo?: string;
  placeholderCc?: string;
  placeholderBcc?: string;
  toLabel?: React.ReactNode;
  bccExtra?: React.ReactNode;
  /** When 'belowSubject', Cc/Bcc rows are not rendered here; use <RecipientCcBccSection> in Compose above Subject. */
  ccBccLocation?: 'belowTo' | 'belowSubject';
  recipientSuggestions?: ComposeRecipientSuggestion[];
}

export interface RecipientCcBccSectionProps {
  to: string;
  cc: string;
  bcc: string;
  onChangeTo: (value: string) => void;
  onChangeCc: (value: string) => void;
  onChangeBcc: (value: string) => void;
  showCc: boolean;
  showBcc: boolean;
  placeholderCc?: string;
  placeholderBcc?: string;
  bccExtra?: React.ReactNode;
  disabled?: boolean;
  recipientSuggestions?: ComposeRecipientSuggestion[];
}

const DRAG_TYPE = 'application/x-recipient-pill';

function getFieldLabel(field: RecipientField): string {
  switch (field) {
    case 'to': return 'To';
    case 'cc': return 'Cc';
    case 'bcc': return 'Bcc';
  }
}

interface RecipientInsetFieldShellProps {
  label: string;
  colors: ThemeColors;
  children: React.ReactNode;
  labelWidthClass?: string;
  dataField?: string;
  isDragOver?: boolean;
  hasValidationIssue?: boolean;
  invalid?: boolean;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  trailing?: React.ReactNode;
}

export const RecipientInsetFieldShell: React.FC<RecipientInsetFieldShellProps> = ({
  label,
  colors,
  children,
  labelWidthClass = 'w-11',
  dataField,
  isDragOver = false,
  hasValidationIssue = false,
  invalid = false,
  onDragOver,
  onDragLeave,
  onDrop,
  trailing,
}) => (
  <div
    className={cn(
      'flex min-h-[2.25rem] rounded-md border overflow-hidden transition-all duration-150',
      'bg-gray-50/50 dark:bg-gray-800/30',
      colors.border,
      invalid && 'ring-2 ring-offset-0 ring-gray-400 dark:ring-gray-500',
      hasValidationIssue && 'ring-2 ring-offset-0 ring-orange-400 dark:ring-orange-500',
      isDragOver && 'ring-2 ring-offset-1 ring-primary/40 border-primary/60 bg-primary/5 dark:bg-primary/10',
    )}
  >
    <span
      className={cn(
        'flex items-center justify-center shrink-0 self-stretch border-r text-[11px] font-semibold uppercase tracking-wider select-none px-1',
        labelWidthClass,
        colors.border,
        colors.textMuted,
        'bg-gray-100/80 dark:bg-gray-900/50',
      )}
    >
      {label}
    </span>
    <div className="flex-1 min-w-0 flex items-center gap-1 px-2 py-1">
      <div
        className="flex-1 min-w-0 flex flex-wrap items-center gap-1"
        {...(dataField ? { 'data-recipient-field': dataField } : {})}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {children}
      </div>
      {trailing ? (
        <div className="shrink-0 self-center pl-1" onMouseDown={(e) => e.stopPropagation()}>
          {trailing}
        </div>
      ) : null}
    </div>
  </div>
);

interface CcBccToggleLinksProps {
  showCc: boolean;
  showBcc: boolean;
  onToggleCc: () => void;
  onToggleBcc: () => void;
  colors: ThemeColors;
}

const CcBccToggleLinks: React.FC<CcBccToggleLinksProps> = ({
  showCc,
  showBcc,
  onToggleCc,
  onToggleBcc,
  colors,
}) => (
  <div className="flex items-center gap-1.5 text-xs font-semibold">
    <button
      type="button"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        onToggleCc();
      }}
      className={cn(
        'px-2 py-0.5 rounded-md transition-colors',
        showCc
          ? cn('font-bold', colors.primaryFg, 'bg-primary/15 dark:bg-primary/25')
          : cn('font-semibold', colors.primaryFg, 'hover:bg-primary/10 dark:hover:bg-primary/20'),
      )}
    >
      Cc
    </button>
    <span className={cn('opacity-30', colors.textMuted)} aria-hidden>
      |
    </span>
    <button
      type="button"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        onToggleBcc();
      }}
      className={cn(
        'px-2 py-0.5 rounded-md transition-colors',
        showBcc
          ? cn('font-bold', colors.primaryFg, 'bg-primary/15 dark:bg-primary/25')
          : cn('font-semibold', colors.primaryFg, 'hover:bg-primary/10 dark:hover:bg-primary/20'),
      )}
    >
      Bcc
    </button>
  </div>
);

export const RecipientSelector: React.FC<RecipientSelectorProps> = ({
  to,
  cc,
  bcc,
  onChangeTo,
  onChangeCc,
  onChangeBcc,
  toInvalid = false,
  disabled = false,
  showCc,
  showBcc,
  onToggleCc,
  onToggleBcc,
  placeholderTo = 'Recipients',
  placeholderCc = 'Cc',
  placeholderBcc = 'Bcc',
  toLabel,
  bccExtra,
  ccBccLocation = 'belowTo',
  recipientSuggestions = [],
}) => {
  const { colors } = useTheme();
  const [inputTo, setInputTo] = useState('');
  const [inputCc, setInputCc] = useState('');
  const [inputBcc, setInputBcc] = useState('');
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    email: string;
    field: RecipientField;
  } | null>(null);
  const [editingPill, setEditingPill] = useState<{
    field: RecipientField;
    index: number;
    value: string;
  } | null>(null);
  const [dragOverField, setDragOverField] = useState<RecipientField | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const pillsTo = parseEmails(to);
  const pillsCc = parseEmails(cc);
  const pillsBcc = parseEmails(bcc);
  const allTokens = useMemo(() => [...parseEmails(to), ...parseEmails(cc), ...parseEmails(bcc)], [to, cc, bcc]);
  const suggestions = useRecipientSuggestionUi(recipientSuggestions, allTokens);

  const commitInput = useCallback(
    (field: RecipientField, inputValue: string, setInput: (v: string) => void) => {
      const trimmed = inputValue.trim();
      if (!trimmed) return;
      const newEmails = parseEmails(trimmed);
      const setter = field === 'to' ? onChangeTo : field === 'cc' ? onChangeCc : onChangeBcc;
      const current = field === 'to' ? to : field === 'cc' ? cc : bcc;
      const existing = parseEmails(current);
      const combined = [...existing];
      for (const e of newEmails) {
        if (e && !combined.some((x) => x.toLowerCase() === e.toLowerCase())) combined.push(e);
      }
      setter(joinEmails(combined));
      setInput('');
      suggestions.close();
    },
    [onChangeTo, onChangeCc, onChangeBcc, to, cc, bcc, suggestions],
  );

  const commitSuggestion = useCallback(
    (field: RecipientField, item: ComposeRecipientSuggestion, setInput: (v: string) => void) => {
      const token = suggestions.formatSelection(item);
      const bare = (extractRecipientEmail(token) ?? item.email).toLowerCase();
      const setter = field === 'to' ? onChangeTo : field === 'cc' ? onChangeCc : onChangeBcc;
      const current = field === 'to' ? to : field === 'cc' ? cc : bcc;
      const existing = parseEmails(current);
      if (
        existing.some((x) => (extractRecipientEmail(x) ?? x).trim().toLowerCase() === bare)
      ) {
        setInput('');
        suggestions.close();
        return;
      }
      setter(joinEmails([...existing, token]));
      setInput('');
      suggestions.close();
    },
    [onChangeTo, onChangeCc, onChangeBcc, to, cc, bcc, suggestions],
  );

  const removePill = useCallback(
    (field: RecipientField, index: number) => {
      const current = field === 'to' ? to : field === 'cc' ? cc : bcc;
      const arr = parseEmails(current).filter((_, i) => i !== index);
      const setter = field === 'to' ? onChangeTo : field === 'cc' ? onChangeCc : onChangeBcc;
      setter(joinEmails(arr));
    },
    [to, cc, bcc, onChangeTo, onChangeCc, onChangeBcc]
  );

  const startPillEdit = useCallback((field: RecipientField, index: number, value: string) => {
    setEditingPill({ field, index, value });
    setContextMenu(null);
  }, []);

  const savePillEdit = useCallback(
    (field: RecipientField) => {
      if (!editingPill || editingPill.field !== field) return;
      const trimmed = editingPill.value.trim();
      if (!trimmed) {
        removePill(field, editingPill.index);
        setEditingPill(null);
        return;
      }
      const current = field === 'to' ? to : field === 'cc' ? cc : bcc;
      const arr = parseEmails(current);
      if (editingPill.index < 0 || editingPill.index >= arr.length) {
        setEditingPill(null);
        return;
      }
      arr[editingPill.index] = trimmed;
      const setter = field === 'to' ? onChangeTo : field === 'cc' ? onChangeCc : onChangeBcc;
      setter(joinEmails(arr));
      setEditingPill(null);
    },
    [editingPill, to, cc, bcc, onChangeTo, onChangeCc, onChangeBcc, removePill],
  );

  const movePill = useCallback(
    (email: string, fromField: RecipientField, toField: RecipientField) => {
      if (fromField === toField) return;
      const fromVal = fromField === 'to' ? to : fromField === 'cc' ? cc : bcc;
      const toVal = toField === 'to' ? to : toField === 'cc' ? cc : bcc;
      const fromArr = parseEmails(fromVal).filter((e) => e.toLowerCase() !== email.toLowerCase());
      let toArr = parseEmails(toVal);
      if (!toArr.some((e) => e.toLowerCase() === email.toLowerCase())) toArr = [...toArr, email];
      const setFrom = fromField === 'to' ? onChangeTo : fromField === 'cc' ? onChangeCc : onChangeBcc;
      const setTo = toField === 'to' ? onChangeTo : toField === 'cc' ? onChangeCc : onChangeBcc;
      setFrom(joinEmails(fromArr));
      setTo(joinEmails(toArr));
    },
    [to, cc, bcc, onChangeTo, onChangeCc, onChangeBcc]
  );

  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('scroll', close, true);
    };
  }, [contextMenu]);

  const handleKeyDown = useCallback(
    (field: RecipientField, e: React.KeyboardEvent<HTMLInputElement>) => {
      const input = field === 'to' ? inputTo : field === 'cc' ? inputCc : inputBcc;
      const setInput = field === 'to' ? setInputTo : field === 'cc' ? setInputCc : setInputBcc;
      const val = field === 'to' ? to : field === 'cc' ? cc : bcc;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!suggestions.open || suggestions.activeField !== field) {
          suggestions.openForField(field, input);
        } else {
          suggestions.moveHighlight(1);
        }
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (!suggestions.open || suggestions.activeField !== field) {
          suggestions.openForField(field, input);
        } else {
          suggestions.moveHighlight(-1);
        }
        return;
      }
      if (e.key === 'Escape') {
        if (suggestions.open && suggestions.activeField === field) {
          e.preventDefault();
          suggestions.close();
        }
        return;
      }
      if (e.key === 'Enter') {
        if (
          suggestions.open &&
          suggestions.activeField === field &&
          suggestions.highlightedItem
        ) {
          e.preventDefault();
          commitSuggestion(field, suggestions.highlightedItem, setInput);
          return;
        }
        if (input.trim()) {
          e.preventDefault();
          commitInput(field, input, setInput);
        }
        return;
      }
      if (e.key === 'Tab') {
        if (input.trim()) {
          e.preventDefault();
          commitInput(field, input, setInput);
        }
        return;
      }
      if (e.key === 'Backspace') {
        const pills = parseEmails(val);
        if (pills.length > 0 && !input) {
          e.preventDefault();
          removePill(field, pills.length - 1);
        }
      }
    },
    [
      inputTo,
      inputCc,
      inputBcc,
      to,
      cc,
      bcc,
      commitInput,
      commitSuggestion,
      removePill,
      suggestions,
    ],
  );

  const handleDrop = useCallback(
    (targetField: RecipientField, e: React.DragEvent) => {
      e.preventDefault();
      setDragOverField(null);
      const raw = e.dataTransfer.getData(DRAG_TYPE);
      if (!raw) return;
      try {
        const { email, field: sourceField } = JSON.parse(raw) as { email: string; field: RecipientField };
        movePill(email, sourceField, targetField);
      } catch {
        // ignore
      }
    },
    [movePill]
  );

  const handleDragOver = useCallback((field: RecipientField) => (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverField(field);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverField(null);
  }, []);

  const renderRow = (
    field: RecipientField,
    value: string,
    pills: string[],
    inputValue: string,
    setInputValue: (v: string) => void,
    placeholder: string,
    label: React.ReactNode,
    required?: boolean,
    invalid?: boolean,
    rowExtra?: React.ReactNode
  ) => {
    const isTo = field === 'to';
    const isDragOver = dragOverField === field;
    const hasInvalidPill = pills.some((email) => !isValidRecipientToken(email));
    const inputLooksInvalid = Boolean(inputValue.trim()) && !isValidRecipientToken(inputValue.trim());
    const hasValidationIssue = hasInvalidPill || inputLooksInvalid;

    const fieldContent = (
      <>
          {pills.map((email, i) => {
            if (editingPill?.field === field && editingPill.index === i) {
              return (
                <RecipientPillEditInput
                  key={`${field}-edit-${i}`}
                  value={editingPill.value}
                  onChange={(next) => setEditingPill({ field, index: i, value: next })}
                  onBlur={() => savePillEdit(field)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      savePillEdit(field);
                    }
                    if (e.key === 'Escape') setEditingPill(null);
                  }}
                  ariaLabel={`Edit ${email}`}
                  colors={colors}
                />
              );
            }
            return (
              <Pill
                key={`${field}-${i}-${email}`}
                email={email}
                field={field}
                invalid={!isValidRecipientToken(email)}
                onRemove={() => removePill(field, i)}
                onDoubleClick={() => {
                  if (!(disabled && isTo)) startPillEdit(field, i, email);
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setContextMenu({ x: e.clientX, y: e.clientY, email, field });
                }}
                onDragStart={(e) => {
                  e.dataTransfer.setData(DRAG_TYPE, JSON.stringify({ email, field }));
                  e.dataTransfer.effectAllowed = 'move';
                }}
                disabled={disabled && isTo}
                colors={colors}
              />
            );
          })}
          <input
            type="text"
            value={inputValue}
            onChange={(e) => {
              const next = e.target.value;
              setInputValue(next);
              suggestions.syncQuery(field, next);
            }}
            onKeyDown={(e) => handleKeyDown(field, e)}
            onFocus={() => suggestions.openForField(field, inputValue)}
            onBlur={() => {
              suggestions.close();
              if (inputValue.trim()) commitInput(field, inputValue, setInputValue);
            }}
            placeholder={pills.length === 0 ? placeholder : ''}
            className={cn(
              'flex-1 min-w-[10rem] bg-transparent border-0 outline-none text-sm py-0.5 placeholder-gray-400 dark:placeholder-gray-500',
              colors.text,
              inputLooksInvalid && 'text-orange-700 dark:text-orange-300',
            )}
            disabled={disabled && isTo}
            aria-label={getFieldLabel(field)}
            aria-invalid={inputLooksInvalid || hasInvalidPill || undefined}
            aria-autocomplete="list"
            aria-expanded={suggestions.open && suggestions.activeField === field}
            aria-controls={
              suggestions.open && suggestions.activeField === field ? suggestions.listId : undefined
            }
            aria-activedescendant={
              suggestions.open &&
              suggestions.activeField === field &&
              suggestions.filtered.flat.length > 0
                ? recipientSuggestionOptionId(suggestions.listId, suggestions.highlightedIndex)
                : undefined
            }
            role="combobox"
          />
      </>
    );

    return (
      <div className="relative">
        {rowExtra && (
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            {rowExtra}
          </div>
        )}
        <RecipientInsetFieldShell
          label={required ? `${String(label)}*` : String(label)}
          dataField={field}
          colors={colors}
          isDragOver={isDragOver}
          hasValidationIssue={hasValidationIssue}
          invalid={Boolean(invalid)}
          onDragOver={handleDragOver(field)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(field, e)}
          trailing={
            isTo ? (
              <CcBccToggleLinks
                showCc={showCc}
                showBcc={showBcc}
                onToggleCc={onToggleCc}
                onToggleBcc={onToggleBcc}
                colors={colors}
              />
            ) : undefined
          }
        >
          {fieldContent}
        </RecipientInsetFieldShell>
        {suggestions.open && suggestions.activeField === field && (
          <RecipientSuggestionList
            listId={suggestions.listId}
            filtered={suggestions.filtered}
            highlightedIndex={suggestions.highlightedIndex}
            onHighlight={suggestions.setHighlightedIndex}
            onSelect={(item) => commitSuggestion(field, item, setInputValue)}
          />
        )}
      </div>
    );
  };

  return (
    <>
      <div className="space-y-0">
        {renderRow(
          'to',
          to,
          pillsTo,
          inputTo,
          setInputTo,
          placeholderTo,
          toLabel ?? 'To',
          true,
          toInvalid,
          undefined
        )}

        {ccBccLocation === 'belowTo' && showCc &&
          renderRow('cc', cc, pillsCc, inputCc, setInputCc, placeholderCc, 'Cc', false, false, undefined)}
        {ccBccLocation === 'belowTo' && showBcc &&
          renderRow(
            'bcc',
            bcc,
            pillsBcc,
            inputBcc,
            setInputBcc,
            placeholderBcc,
            'Bcc',
            false,
            false,
            bccExtra
          )}
      </div>

      {contextMenu && (
        <div
          ref={menuRef}
          className={cn(
            'fixed py-1.5 min-w-[11rem] rounded-xl shadow-xl border',
            colors.surface,
            colors.border
          )}
          style={{ left: contextMenu.x, top: contextMenu.y, ...zStyle(Z_INDEX.portaledDropdown) }}
        >
          <div className={cn('px-3 py-2 text-xs truncate max-w-[14rem] border-b', colors.textMuted, colors.border)}>
            {contextMenu.email}
          </div>
          <div className="py-1">
            {contextMenu.field !== 'to' && (
              <button
                type="button"
                className={cn('w-full text-left px-3 py-2 text-sm rounded-lg mx-1', colors.text, colors.interactiveHover)}
                onClick={() => {
                  movePill(contextMenu!.email, contextMenu!.field, 'to');
                  setContextMenu(null);
                }}
              >
                Move to To
              </button>
            )}
            {contextMenu.field !== 'cc' && (
              <button
                type="button"
                className={cn('w-full text-left px-3 py-2 text-sm rounded-lg mx-1', colors.text, colors.interactiveHover)}
                onClick={() => {
                  movePill(contextMenu!.email, contextMenu!.field, 'cc');
                  setContextMenu(null);
                }}
              >
                Move to Cc
              </button>
            )}
            {contextMenu.field !== 'bcc' && (
              <button
                type="button"
                className={cn('w-full text-left px-3 py-2 text-sm rounded-lg mx-1', colors.text, colors.interactiveHover)}
                onClick={() => {
                  movePill(contextMenu!.email, contextMenu!.field, 'bcc');
                  setContextMenu(null);
                }}
              >
                Move to Bcc
              </button>
            )}
            <div className={cn('border-t my-1', colors.border)} />
            <button
              type="button"
              className={cn('w-full text-left px-3 py-2 text-sm rounded-lg mx-1', colors.text, colors.interactiveHover)}
              onClick={() => {
                const arr = parseEmails(
                  contextMenu!.field === 'to' ? to : contextMenu!.field === 'cc' ? cc : bcc
                );
                const idx = arr.findIndex((e) => e.toLowerCase() === contextMenu!.email.toLowerCase());
                if (idx >= 0) startPillEdit(contextMenu!.field, idx, contextMenu!.email);
              }}
            >
              Edit address
            </button>
            <button
              type="button"
              className={cn('w-full text-left px-3 py-2 text-sm rounded-lg mx-1', colors.textMuted, colors.interactiveHover)}
              onClick={() => {
                const arr = parseEmails(
                  contextMenu!.field === 'to' ? to : contextMenu!.field === 'cc' ? cc : bcc
                );
                const idx = arr.findIndex((e) => e.toLowerCase() === contextMenu!.email.toLowerCase());
                if (idx >= 0) removePill(contextMenu!.field, idx);
                setContextMenu(null);
              }}
            >
              Remove
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export const RecipientCcBccSection: React.FC<RecipientCcBccSectionProps> = ({
  to,
  cc,
  bcc,
  onChangeTo,
  onChangeCc,
  onChangeBcc,
  showCc,
  showBcc,
  placeholderCc = 'Cc',
  placeholderBcc = 'Bcc',
  bccExtra,
  disabled = false,
  recipientSuggestions = [],
}) => {
  const { colors } = useTheme();
  const [inputCc, setInputCc] = useState('');
  const [inputBcc, setInputBcc] = useState('');
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    email: string;
    field: RecipientField;
  } | null>(null);
  const [editingPill, setEditingPill] = useState<{
    field: RecipientField;
    index: number;
    value: string;
  } | null>(null);
  const [dragOverField, setDragOverField] = useState<RecipientField | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const pillsCc = parseEmails(cc);
  const pillsBcc = parseEmails(bcc);
  const allTokens = useMemo(
    () => [...parseEmails(to), ...parseEmails(cc), ...parseEmails(bcc)],
    [to, cc, bcc],
  );
  const suggestions = useRecipientSuggestionUi(recipientSuggestions, allTokens);

  const commitInput = useCallback(
    (field: RecipientField, inputValue: string, setInput: (v: string) => void) => {
      const trimmed = inputValue.trim();
      if (!trimmed) return;
      const newEmails = parseEmails(trimmed);
      const setter = field === 'to' ? onChangeTo : field === 'cc' ? onChangeCc : onChangeBcc;
      const current = field === 'to' ? to : field === 'cc' ? cc : bcc;
      const existing = parseEmails(current);
      const combined = [...existing];
      for (const e of newEmails) {
        if (e && !combined.some((x) => x.toLowerCase() === e.toLowerCase())) combined.push(e);
      }
      setter(joinEmails(combined));
      setInput('');
      suggestions.close();
    },
    [onChangeTo, onChangeCc, onChangeBcc, to, cc, bcc, suggestions],
  );

  const commitSuggestion = useCallback(
    (field: RecipientField, item: ComposeRecipientSuggestion, setInput: (v: string) => void) => {
      const token = suggestions.formatSelection(item);
      const bare = (extractRecipientEmail(token) ?? item.email).toLowerCase();
      const setter = field === 'to' ? onChangeTo : field === 'cc' ? onChangeCc : onChangeBcc;
      const current = field === 'to' ? to : field === 'cc' ? cc : bcc;
      const existing = parseEmails(current);
      if (
        existing.some((x) => (extractRecipientEmail(x) ?? x).trim().toLowerCase() === bare)
      ) {
        setInput('');
        suggestions.close();
        return;
      }
      setter(joinEmails([...existing, token]));
      setInput('');
      suggestions.close();
    },
    [onChangeTo, onChangeCc, onChangeBcc, to, cc, bcc, suggestions],
  );

  const removePill = useCallback(
    (field: RecipientField, index: number) => {
      const current = field === 'to' ? to : field === 'cc' ? cc : bcc;
      const arr = parseEmails(current).filter((_, i) => i !== index);
      const setter = field === 'to' ? onChangeTo : field === 'cc' ? onChangeCc : onChangeBcc;
      setter(joinEmails(arr));
    },
    [to, cc, bcc, onChangeTo, onChangeCc, onChangeBcc]
  );

  const startPillEdit = useCallback((field: RecipientField, index: number, value: string) => {
    setEditingPill({ field, index, value });
    setContextMenu(null);
  }, []);

  const savePillEdit = useCallback(
    (field: RecipientField) => {
      if (!editingPill || editingPill.field !== field) return;
      const trimmed = editingPill.value.trim();
      if (!trimmed) {
        removePill(field, editingPill.index);
        setEditingPill(null);
        return;
      }
      const current = field === 'to' ? to : field === 'cc' ? cc : bcc;
      const arr = parseEmails(current);
      if (editingPill.index < 0 || editingPill.index >= arr.length) {
        setEditingPill(null);
        return;
      }
      arr[editingPill.index] = trimmed;
      const setter = field === 'to' ? onChangeTo : field === 'cc' ? onChangeCc : onChangeBcc;
      setter(joinEmails(arr));
      setEditingPill(null);
    },
    [editingPill, to, cc, bcc, onChangeTo, onChangeCc, onChangeBcc, removePill],
  );

  const movePill = useCallback(
    (email: string, fromField: RecipientField, toField: RecipientField) => {
      if (fromField === toField) return;
      const fromVal = fromField === 'to' ? to : fromField === 'cc' ? cc : bcc;
      const toVal = toField === 'to' ? to : toField === 'cc' ? cc : bcc;
      const fromArr = parseEmails(fromVal).filter((e) => e.toLowerCase() !== email.toLowerCase());
      let toArr = parseEmails(toVal);
      if (!toArr.some((e) => e.toLowerCase() === email.toLowerCase())) toArr = [...toArr, email];
      const setFrom = fromField === 'to' ? onChangeTo : fromField === 'cc' ? onChangeCc : onChangeBcc;
      const setTo = toField === 'to' ? onChangeTo : toField === 'cc' ? onChangeCc : onChangeBcc;
      setFrom(joinEmails(fromArr));
      setTo(joinEmails(toArr));
    },
    [to, cc, bcc, onChangeTo, onChangeCc, onChangeBcc]
  );

  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('scroll', close, true);
    };
  }, [contextMenu]);

  const handleKeyDown = useCallback(
    (field: RecipientField, e: React.KeyboardEvent<HTMLInputElement>) => {
      const input = field === 'cc' ? inputCc : inputBcc;
      const setInput = field === 'cc' ? setInputCc : setInputBcc;
      const val = field === 'cc' ? cc : bcc;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!suggestions.open || suggestions.activeField !== field) {
          suggestions.openForField(field, input);
        } else {
          suggestions.moveHighlight(1);
        }
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (!suggestions.open || suggestions.activeField !== field) {
          suggestions.openForField(field, input);
        } else {
          suggestions.moveHighlight(-1);
        }
        return;
      }
      if (e.key === 'Escape') {
        if (suggestions.open && suggestions.activeField === field) {
          e.preventDefault();
          suggestions.close();
        }
        return;
      }
      if (e.key === 'Enter') {
        if (
          suggestions.open &&
          suggestions.activeField === field &&
          suggestions.highlightedItem
        ) {
          e.preventDefault();
          commitSuggestion(field, suggestions.highlightedItem, setInput);
          return;
        }
        if (input.trim()) {
          e.preventDefault();
          commitInput(field, input, setInput);
        }
        return;
      }
      if (e.key === 'Tab') {
        if (input.trim()) {
          e.preventDefault();
          commitInput(field, input, setInput);
        }
        return;
      }
      if (e.key === 'Backspace') {
        const pills = parseEmails(val);
        if (pills.length > 0 && !input) {
          e.preventDefault();
          removePill(field, pills.length - 1);
        }
      }
    },
    [inputCc, inputBcc, cc, bcc, commitInput, commitSuggestion, removePill, suggestions],
  );

  const handleDrop = useCallback(
    (targetField: RecipientField, e: React.DragEvent) => {
      e.preventDefault();
      setDragOverField(null);
      const raw = e.dataTransfer.getData(DRAG_TYPE);
      if (!raw) return;
      try {
        const { email, field: sourceField } = JSON.parse(raw) as { email: string; field: RecipientField };
        movePill(email, sourceField, targetField);
      } catch {
        // ignore
      }
    },
    [movePill]
  );

  const handleDragOver = useCallback((field: RecipientField) => (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverField(field);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverField(null);
  }, []);

  const renderRow = (
    field: RecipientField,
    value: string,
    pills: string[],
    inputValue: string,
    setInputValue: (v: string) => void,
    placeholder: string,
    label: string,
    rowExtra?: React.ReactNode
  ) => {
    const isDragOver = dragOverField === field;
    const hasInvalidPill = pills.some((email) => !isValidRecipientToken(email));
    const inputLooksInvalid = Boolean(inputValue.trim()) && !isValidRecipientToken(inputValue.trim());
    const hasValidationIssue = hasInvalidPill || inputLooksInvalid;
    return (
      <div className="relative">
        {rowExtra && (
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            {rowExtra}
          </div>
        )}
        <RecipientInsetFieldShell
          label={label}
          dataField={field}
          colors={colors}
          isDragOver={isDragOver}
          hasValidationIssue={hasValidationIssue}
          onDragOver={handleDragOver(field)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(field, e)}
        >
          {pills.map((email, i) => {
            if (editingPill?.field === field && editingPill.index === i) {
              return (
                <RecipientPillEditInput
                  key={`${field}-edit-${i}`}
                  value={editingPill.value}
                  onChange={(next) => setEditingPill({ field, index: i, value: next })}
                  onBlur={() => savePillEdit(field)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      savePillEdit(field);
                    }
                    if (e.key === 'Escape') setEditingPill(null);
                  }}
                  ariaLabel={`Edit ${email}`}
                  colors={colors}
                />
              );
            }
            return (
              <Pill
                key={`${field}-${i}-${email}`}
                email={email}
                field={field}
                invalid={!isValidRecipientToken(email)}
                onRemove={() => removePill(field, i)}
                onDoubleClick={() => {
                  if (!disabled) startPillEdit(field, i, email);
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setContextMenu({ x: e.clientX, y: e.clientY, email, field });
                }}
                onDragStart={(e) => {
                  e.dataTransfer.setData(DRAG_TYPE, JSON.stringify({ email, field }));
                  e.dataTransfer.effectAllowed = 'move';
                }}
                disabled={disabled}
                colors={colors}
              />
            );
          })}
          <input
            type="text"
            value={inputValue}
            onChange={(e) => {
              const next = e.target.value;
              setInputValue(next);
              suggestions.syncQuery(field, next);
            }}
            onKeyDown={(e) => handleKeyDown(field, e)}
            onFocus={() => suggestions.openForField(field, inputValue)}
            onBlur={() => {
              suggestions.close();
              if (inputValue.trim()) commitInput(field, inputValue, setInputValue);
            }}
            placeholder={pills.length === 0 ? placeholder : ''}
            className={cn(
              'flex-1 min-w-[10rem] bg-transparent border-0 outline-none text-sm py-0.5 placeholder-gray-400 dark:placeholder-gray-500',
              colors.text,
              inputLooksInvalid && 'text-orange-700 dark:text-orange-300',
            )}
            aria-label={getFieldLabel(field)}
            aria-invalid={inputLooksInvalid || hasInvalidPill || undefined}
            aria-autocomplete="list"
            aria-expanded={suggestions.open && suggestions.activeField === field}
            aria-controls={
              suggestions.open && suggestions.activeField === field ? suggestions.listId : undefined
            }
            aria-activedescendant={
              suggestions.open &&
              suggestions.activeField === field &&
              suggestions.filtered.flat.length > 0
                ? recipientSuggestionOptionId(suggestions.listId, suggestions.highlightedIndex)
                : undefined
            }
            role="combobox"
          />
        </RecipientInsetFieldShell>
        {suggestions.open && suggestions.activeField === field && (
          <RecipientSuggestionList
            listId={suggestions.listId}
            filtered={suggestions.filtered}
            highlightedIndex={suggestions.highlightedIndex}
            onHighlight={suggestions.setHighlightedIndex}
            onSelect={(item) => commitSuggestion(field, item, setInputValue)}
          />
        )}
      </div>
    );
  };

  if (!showCc && !showBcc) return null;

  return (
    <>
      <div className="space-y-1.5">
        {showCc &&
          renderRow('cc', cc, pillsCc, inputCc, setInputCc, placeholderCc, 'Cc', undefined)}
        {showBcc &&
          renderRow('bcc', bcc, pillsBcc, inputBcc, setInputBcc, placeholderBcc, 'Bcc', bccExtra)}
      </div>

      {contextMenu && (
        <div
          ref={menuRef}
          className={cn(
            'fixed py-1.5 min-w-[11rem] rounded-xl shadow-xl border',
            colors.surface,
            colors.border
          )}
          style={{ left: contextMenu.x, top: contextMenu.y, ...zStyle(Z_INDEX.portaledDropdown) }}
        >
          <div className={cn('px-3 py-2 text-xs truncate max-w-[14rem] border-b', colors.textMuted, colors.border)}>
            {contextMenu.email}
          </div>
          <div className="py-1">
            {contextMenu.field !== 'to' && (
              <button
                type="button"
                className={cn('w-full text-left px-3 py-2 text-sm rounded-lg mx-1', colors.text, colors.interactiveHover)}
                onClick={() => {
                  movePill(contextMenu!.email, contextMenu!.field, 'to');
                  setContextMenu(null);
                }}
              >
                Move to To
              </button>
            )}
            {contextMenu.field !== 'cc' && (
              <button
                type="button"
                className={cn('w-full text-left px-3 py-2 text-sm rounded-lg mx-1', colors.text, colors.interactiveHover)}
                onClick={() => {
                  movePill(contextMenu!.email, contextMenu!.field, 'cc');
                  setContextMenu(null);
                }}
              >
                Move to Cc
              </button>
            )}
            {contextMenu.field !== 'bcc' && (
              <button
                type="button"
                className={cn('w-full text-left px-3 py-2 text-sm rounded-lg mx-1', colors.text, colors.interactiveHover)}
                onClick={() => {
                  movePill(contextMenu!.email, contextMenu!.field, 'bcc');
                  setContextMenu(null);
                }}
              >
                Move to Bcc
              </button>
            )}
            <div className={cn('border-t my-1', colors.border)} />
            <button
              type="button"
              className={cn('w-full text-left px-3 py-2 text-sm rounded-lg mx-1', colors.text, colors.interactiveHover)}
              onClick={() => {
                const arr = parseEmails(
                  contextMenu!.field === 'to' ? to : contextMenu!.field === 'cc' ? cc : bcc
                );
                const idx = arr.findIndex((e) => e.toLowerCase() === contextMenu!.email.toLowerCase());
                if (idx >= 0) startPillEdit(contextMenu!.field, idx, contextMenu!.email);
              }}
            >
              Edit address
            </button>
            <button
              type="button"
              className={cn('w-full text-left px-3 py-2 text-sm rounded-lg mx-1', colors.textMuted, colors.interactiveHover)}
              onClick={() => {
                const arr = parseEmails(
                  contextMenu!.field === 'to' ? to : contextMenu!.field === 'cc' ? cc : bcc
                );
                const idx = arr.findIndex((e) => e.toLowerCase() === contextMenu!.email.toLowerCase());
                if (idx >= 0) removePill(contextMenu!.field, idx);
                setContextMenu(null);
              }}
            >
              Remove
            </button>
          </div>
        </div>
      )}
    </>
  );
};

interface PillProps {
  email: string;
  field: RecipientField;
  invalid?: boolean;
  onRemove: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onDoubleClick?: () => void;
  onDragStart: (e: React.DragEvent) => void;
  disabled?: boolean;
  colors: ThemeColors;
}

const Pill: React.FC<PillProps> = ({
  email,
  invalid = false,
  onRemove,
  onContextMenu,
  onDoubleClick,
  onDragStart,
  disabled,
  colors,
}) => {
  const [dragging, setDragging] = useState(false);
  const canEdit = Boolean(onDoubleClick) && !disabled;
  return (
    <span
      draggable={!disabled}
      onDragStart={(e) => {
        setDragging(true);
        onDragStart(e);
      }}
      onDragEnd={() => setDragging(false)}
      onContextMenu={onContextMenu}
      className={cn(
        'inline-flex items-center gap-0.5 rounded-md pl-1.5 pr-1 py-0.5 text-[11px] font-medium select-none transition-opacity',
        canEdit ? 'cursor-text' : 'cursor-grab active:cursor-grabbing',
        invalid
          ? 'bg-orange-100 dark:bg-orange-950/70 border border-orange-400 dark:border-orange-500 ring-1 ring-orange-300/70 dark:ring-orange-600/70'
          : 'bg-gray-200/80 dark:bg-gray-600/80 border border-gray-300/80 dark:border-gray-500/80',
        dragging && 'opacity-50',
        !disabled && !invalid && 'hover:bg-gray-300/80 dark:hover:bg-gray-500/80',
        !disabled && invalid && 'hover:bg-orange-200/90 dark:hover:bg-orange-900/70',
      )}
      title={
        invalid
          ? `Invalid email address: ${email}`
          : canEdit
            ? `${email} — double-click to edit`
            : email
      }
    >
      {invalid && (
        <span className="text-orange-600 dark:text-orange-300 text-[10px] font-bold leading-none" aria-hidden>
          !
        </span>
      )}
      <span
        className={cn(
          'max-w-[12rem] truncate',
          invalid ? 'text-orange-950 dark:text-orange-50' : colors.text,
        )}
        onDoubleClick={(e) => {
          if (!canEdit) return;
          e.preventDefault();
          e.stopPropagation();
          onDoubleClick?.();
        }}
      >
        {email}
      </span>
      {!disabled && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className={cn(
            'rounded-sm p-0.5 leading-none min-w-[1.35rem] min-h-[1.35rem] flex items-center justify-center text-sm font-bold opacity-70 hover:opacity-100 transition-opacity',
            colors.textMuted,
            'hover:bg-gray-400/30 dark:hover:bg-gray-400/30'
          )}
          aria-label={`Remove ${email}`}
        >
          ×
        </button>
      )}
    </span>
  );
};

export default RecipientSelector;
